import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import ContentRow from '@/components/ContentRow';
import { contentfulVideoService } from '@/services/contentfulVideoService';
import { ContentCategory } from '@/types/content';
import { Content } from '@/types/content';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import VideoFeaturedSection from '@/components/video/FeaturedVideosRow';
import { useQuery } from '@tanstack/react-query';
const VideoLibrary = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [videoCategories, setVideoCategories] = useState<ContentCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [filteredCategories, setFilteredCategories] = useState<ContentCategory[]>([]);
  const isMobile = useIsMobile();
  const {
    resolvedTheme
  } = useTheme();

  // Get featured videos for the featured section
  const {
    data: featuredVideos = []
  } = useQuery({
    queryKey: ['featuredVideos'],
    queryFn: () => {
      return contentfulVideoService.getFeaturedVideos().then(videos => {
        return videos.map(video => ({
          ...video,
          id: video.id || '',
          thumbnail: video.thumbnail || video.thumbnailUrl || ''
        }));
      });
    },
    staleTime: 5 * 60 * 1000
  });
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setActiveCategory(categoryParam.toLowerCase());
    } else {
      setActiveCategory('all');
    }
  }, [location.search]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const categories = await contentfulVideoService.getVideoCategories();
        const categoriesWithThumbnail = categories.map(category => ({
          ...category,
          contents: category.contents.map(content => ({
            ...content,
            thumbnail: content.thumbnail || content.thumbnailUrl || ''
          }))
        }));

        // Reorder categories in the specified order
        const categoryOrder = ['Architecture', 'Mediterranean', 'Artists', 'Cars'];
        const orderedCategories = [];

        // Add categories in the specified order
        categoryOrder.forEach(orderName => {
          const category = categoriesWithThumbnail.find(cat => cat.name.toLowerCase() === orderName.toLowerCase());
          if (category) {
            orderedCategories.push(category);
          }
        });

        // Add any remaining categories that weren't in the specified order
        categoriesWithThumbnail.forEach(category => {
          if (!orderedCategories.find(cat => cat.id === category.id)) {
            orderedCategories.push(category);
          }
        });
        setVideoCategories(orderedCategories);
        if (activeCategory === 'all') {
          setFilteredCategories(orderedCategories);
        } else {
          const selectedCategory = orderedCategories.find(cat => cat.name.toLowerCase() === activeCategory.toLowerCase());
          setFilteredCategories(selectedCategory ? [selectedCategory] : []);
        }
      } catch (error) {
        console.error('Error fetching video data:', error);
        toast.error('Failed to load videos. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [activeCategory]);
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    const url = new URL(window.location.href);
    if (category === 'all') {
      url.searchParams.delete('category');
    } else {
      url.searchParams.set('category', category);
    }
    window.history.pushState({}, '', url);
    // Scroll to top to show thumbnails (instant for navigation)
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  };
  if (isLoading) {
    return <Layout>
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="w-16 h-16 border-t-4 border-primary rounded-full animate-spin"></div>
        </div>
      </Layout>;
  }
  return <Layout>
      <div className="relative z-10 py-0">
        <div className="px-4 md:px-[50px] lg:px-[50px]">
          {/* Categories Filter */}
          <div className={cn("mb-8 sticky top-16 z-40 backdrop-blur-md border-b border-border/40 pb-3 pt-2 md:top-0 md:pb-4 md:z-50 -mx-4 md:-mx-[50px] lg:-mx-[50px] px-4 md:px-[50px] lg:px-[50px]", resolvedTheme === 'dark' ? 'bg-black' : 'bg-background')}>
            <ScrollArea orientation="horizontal" className="w-full pb-4 bleed-right-mobile [&_.min-w-max]:pr-0 mx-0">
              <div className="flex items-center space-x-3 px-1 min-w-max">
                <Button variant={activeCategory === 'all' ? "secondary" : "outline"} size="sm" onClick={() => handleCategoryChange('all')} className={cn("whitespace-nowrap rounded-full border hover:bg-muted transition-colors", activeCategory === 'all' ? "bg-secondary text-secondary-foreground font-medium" : "bg-transparent hover:text-foreground")}>
                  All Videos
                </Button>
                {videoCategories.map(category => <Button key={category.id} variant={activeCategory === category.name.toLowerCase() ? "secondary" : "outline"} size="sm" onClick={() => handleCategoryChange(category.name.toLowerCase())} className={cn("whitespace-nowrap rounded-full border hover:bg-muted transition-colors", activeCategory === category.name.toLowerCase() ? "bg-secondary text-secondary-foreground font-medium" : "bg-transparent hover:text-foreground")}>
                    {category.name}
                  </Button>)}
              </div>
            </ScrollArea>
          </div>

          {/* Featured Videos Section - Show only when activeCategory is 'all' */}
          {activeCategory === 'all' && featuredVideos.length > 0 && <div className="mb-10">
              <VideoFeaturedSection featuredVideos={featuredVideos as Content[]} />
            </div>}

          {/* Video Grid */}
          <div className="space-y-10 bleed-right-mobile">
            {filteredCategories.map(category => <div key={category.id}>
                {activeCategory === 'all' && <div className="flex items-center justify-between mb-4">
                    <h2 className={cn("text-lg md:text-xl font-medium", resolvedTheme === 'dark' ? 'text-white' : 'text-black')}>
                      {category.name}
                    </h2>
                    <span className={cn("text-sm font-medium mr-4", resolvedTheme === 'dark' ? 'text-gray-400' : 'text-gray-600')}>
                      {category.contents.length} {category.contents.length === 1 ? 'video' : 'videos'}
                    </span>
                  </div>}
                <ContentRow category={category} />
              </div>)}
          </div>

          {filteredCategories.length === 0 && <div className="text-center py-12">
              <p className={cn("text-lg", resolvedTheme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                No videos found in this category
              </p>
            </div>}
        </div>
      </div>
    </Layout>;
};
export default VideoLibrary;